import type { AppEnvironment } from "./store";

type JsonRecord = Record<string, unknown>;

const NAVY = "173A5A";
const GOLD = "D2A334";
const PALE_GOLD = "FFF3CF";
const PALE_BLUE = "EAF3F8";
const WHITE = "FFFFFF";
const TEXT = "0B2E52";

const moduleTitles: Record<string, string> = {
  designs: "JEWELRY DESIGNS EXPORT",
  jobCards: "PRODUCTION JOB CARDS EXPORT",
  inventory: "INVENTORY EXPORT",
  diamondStock: "DIAMOND STOCK EXPORT",
  customers: "CUSTOMERS EXPORT",
  suppliers: "SUPPLIERS EXPORT",
  receivedInvoices: "RECEIVED INVOICES EXPORT",
  pettyCash: "PETTY CASH EXPORT",
  shopify: "ESHOP MANAGEMENT — PRODUCT CATALOG",
  metalPrices: "GOLD & SILVER RATES EXPORT"
};

function scalar(value: unknown) {
  return value == null || ["string", "number", "boolean"].includes(typeof value);
}

function columnsFor(rows: JsonRecord[]) {
  const columns: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!columns.includes(key) && scalar(row[key])) columns.push(key);
    }
  }
  return columns;
}

export const DIAMOND_STOCK_EXPORT_COLUMN_ORDER = [
  "id", "provider_key", "stock_number", "shape", "carat", "color", "fancy_color", "clarity", "cut_grade",
  "polish", "symmetry", "fluorescence", "lab", "certificate_no", "certificate_url", "location", "shade",
  "luster", "eye_clean", "hearts_arrows", "rap_price", "discount_percent", "price_per_carat", "total_price",
  "depth_percent", "table_percent", "measurements", "image_url", "video_url", "availability_status",
  "provider_updated_at", "first_seen_at", "last_seen_at", "synced_at", "active", "internal_status",
  "octadiam_desc", "octadiam_price_per_carat", "octadiam_total_price", "margin", "internal_record_id",
  "reserved_sold_for", "linked_design_id", "inventory_stone_id", "lifecycle_notes", "lifecycle_changed_at"
] as const;

const restrictedDiamondExportColumns = new Set([
  "certificate_no", "certificate_url", "rap_price", "discount_percent", "price_per_carat", "total_price", "margin"
]);

export function diamondStockExportColumns(rows: JsonRecord[], canViewRestricted: boolean) {
  const discovered = columnsFor(rows);
  const ordered = [...DIAMOND_STOCK_EXPORT_COLUMN_ORDER, ...discovered.filter((key) =>
    !(DIAMOND_STOCK_EXPORT_COLUMN_ORDER as readonly string[]).includes(key))];
  return ordered.filter((key) => canViewRestricted || (!restrictedDiamondExportColumns.has(key) &&
    !/^supplier_(discount|price|total)/i.test(key)));
}

function humanize(value: string) {
  const labels: Record<string, string> = {
    octadiam_desc: "Desc %",
    availability_status: "Source status",
    internal_status: "Internal status",
    octadiam_discount_percent: "Desc %",
    octadiam_price_per_carat: "Price / ct $",
    octadiam_total_price: "Total price $",
    image_url: "Image URL",
    video_url: "Video URL",
    certificate_url: "Certificate URL"
  };
  if (labels[value]) return labels[value];
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function safeName(value: string) {
  return value.replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 80);
}

function timestamp() {
  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const time = `${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
  return `${date}_${time}`;
}

function base64FromBuffer(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let index = 0; index < bytes.byteLength; index += 1) binary += String.fromCharCode(bytes[index]);
  return `data:image/png;base64,${btoa(binary)}`;
}

export async function buildBrandedWorkbook({
  moduleKey,
  rows,
  exportedBy,
  environment,
  columns: selectedColumns
}: {
  moduleKey: string;
  rows: JsonRecord[];
  exportedBy: string;
  environment: AppEnvironment;
  columns?: string[];
}) {
  if (!rows.length) throw new Error("There are no visible records to export.");
  const ExcelJS = (await import("exceljs")).default;
  const available = columnsFor(rows);
  const columns = selectedColumns?.length
    ? [...new Set(selectedColumns)]
    : available;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "OctaDiam Operations Hub";
  workbook.company = "OctaDiam";
  workbook.created = new Date();
  const sheet = workbook.addWorksheet("Export", {
    properties: { defaultRowHeight: 20 },
    views: [{ state: "frozen", ySplit: 11 }]
  });
  const lastColumn = Math.max(columns.length + 1, 8);
  const letter = sheet.getColumn(lastColumn).letter;

  for (let row = 1; row <= 4; row += 1) {
    for (let column = 1; column <= lastColumn; column += 1) {
      sheet.getCell(row, column).fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
      sheet.getCell(row, column).border = {};
    }
  }
  try {
    const logoResponse = await fetch("/octadiam-logo.png");
    if (logoResponse.ok) {
      const logoId = workbook.addImage({
        base64: base64FromBuffer(await logoResponse.arrayBuffer()),
        extension: "png"
      });
      sheet.addImage(logoId, { tl: { col: 0.1, row: 0.15 }, ext: { width: 270, height: 74 } });
    }
  } catch {
    sheet.getCell("A1").value = "OCTADIAM";
    sheet.getCell("A1").font = { color: { argb: WHITE }, bold: true, size: 28 };
  }

  sheet.mergeCells(`A4:${letter}4`);
  const company = sheet.getCell("A4");
  company.value = "Gold & Diamond Park, Dubai, United Arab Emirates  •  www.octadiam.ae  •  +971 55 557 1177";
  company.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
  company.font = { color: { argb: WHITE }, size: 10 };
  company.alignment = { vertical: "middle" };

  const titleStart = Math.max(4, lastColumn - 5);
  const logoEnd = Math.max(1, titleStart - 1);
  sheet.mergeCells(1, 1, 3, logoEnd);
  sheet.mergeCells(1, titleStart, 2, lastColumn);
  const title = sheet.getCell(1, titleStart);
  title.value = moduleTitles[moduleKey] || `${humanize(moduleKey).toUpperCase()} EXPORT`;
  title.font = { color: { argb: WHITE }, bold: true, size: 22 };
  title.alignment = { horizontal: "right", vertical: "middle" };
  sheet.mergeCells(3, titleStart, 3, lastColumn);
  const subtitle = sheet.getCell(3, titleStart);
  subtitle.value = `Current filtered list · ${rows.length.toLocaleString()} record${rows.length === 1 ? "" : "s"}`;
  subtitle.font = { color: { argb: WHITE }, size: 11 };
  subtitle.alignment = { horizontal: "right", vertical: "middle" };

  sheet.mergeCells(`A5:${letter}5`);
  const details = sheet.getCell("A5");
  details.value = `Exported ${new Date().toLocaleString("en-AE", { timeZone: "Asia/Dubai" })} GST  •  ${exportedBy}  •  ${environment === "production" ? "Production" : "Test"} environment`;
  details.fill = { type: "pattern", pattern: "solid", fgColor: { argb: GOLD } };
  details.font = { color: { argb: WHITE }, bold: true, size: 10 };
  details.alignment = { horizontal: "right", vertical: "middle" };

  const numericValues = rows.flatMap((row) => Object.values(row).filter((value): value is number => typeof value === "number"));
  const totalNumeric = numericValues.reduce((sum, value) => sum + value, 0);
  const metrics = [
    ["RECORDS", rows.length],
    ["VISIBLE FIELDS", columns.length],
    ["NUMERIC VALUES", numericValues.length],
    ["NUMERIC TOTAL", totalNumeric]
  ] as const;
  const metricWidth = Math.max(1, Math.floor(lastColumn / metrics.length));
  metrics.forEach(([label, value], index) => {
    const start = index * metricWidth + 1;
    const end = index === metrics.length - 1 ? lastColumn : Math.min(lastColumn, start + metricWidth - 1);
    sheet.mergeCells(7, start, 7, end);
    sheet.mergeCells(8, start, 8, end);
    const labelCell = sheet.getCell(7, start);
    labelCell.value = label;
    labelCell.font = { color: { argb: "53667A" }, bold: true, size: 10 };
    labelCell.alignment = { horizontal: "center" };
    const valueCell = sheet.getCell(8, start);
    valueCell.value = value;
    valueCell.font = { color: { argb: TEXT }, bold: true, size: 18 };
    valueCell.alignment = { horizontal: "center" };
    valueCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: index === 3 ? PALE_GOLD : WHITE } };
  });

  sheet.mergeCells(`A10:${letter}10`);
  const filters = sheet.getCell("A10");
  filters.value = "FILTERS APPLIED  ·  Current visible filtered list";
  filters.fill = { type: "pattern", pattern: "solid", fgColor: { argb: PALE_BLUE } };
  filters.font = { color: { argb: TEXT }, bold: true, size: 10 };

  const header = sheet.getRow(11);
  header.values = ["SN", ...columns.map(humanize)];
  header.height = 28;
  header.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
    cell.font = { color: { argb: WHITE }, bold: true };
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });
  rows.forEach((row, index) => {
    const target = sheet.addRow([index + 1, ...columns.map((key) => row[key] ?? "")]);
    if (index % 2) target.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F6F9FB" } };
  });
  sheet.autoFilter = { from: { row: 11, column: 1 }, to: { row: 11 + rows.length, column: lastColumn } };
  sheet.getColumn(1).width = 8;
  columns.forEach((key, index) => {
    const width = Math.min(42, Math.max(12, humanize(key).length + 3,
      ...rows.slice(0, 200).map((row) => String(row[key] ?? "").length + 2)));
    sheet.getColumn(index + 2).width = width;
    if (rows.some((row) => typeof row[key] === "number")) {
      sheet.getColumn(index + 2).numFmt = "#,##0.00";
    }
  });
  sheet.pageSetup = { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
  sheet.headerFooter.oddFooter = "&LOctaDiam Operations Hub&CPage &P of &N&RConfidential";

  return workbook;
}

export async function exportBrandedWorkbook(options: {
  moduleKey: string;
  rows: JsonRecord[];
  exportedBy: string;
  environment: AppEnvironment;
  columns?: string[];
}) {
  const workbook = await buildBrandedWorkbook(options);
  const bytes = await workbook.xlsx.writeBuffer();
  const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${safeName(
    moduleTitles[options.moduleKey]?.replace(/ EXPORT$/, "") || options.moduleKey,
  )}_${timestamp()}.xlsx`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}
